import {useState, useCallback, useMemo} from 'react';
import {getOptions} from '../services/documentService.js';
import {
  formatOptions,
  getCountryName,
  createErrorElement,
} from '../utils/utils.js';

export const useFilterOptions = accessToken => {
  const [options, setOptions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAllOptions = useCallback(
    async filters => {
      try {
        setIsLoading(true);
        setError(null);

        const [
          countries,
          documentTypes,
          states,
          documentVersions,
          yearsOfIssue,
          aliases,
          statusCodes,
          supportedFrom,
        ] = await Promise.all([
          getOptions('countryCode', filters, 'no', accessToken),
          getOptions('documentType', filters, 'no', accessToken),
          getOptions('state', filters, 'no', accessToken),
          getOptions('documentVersion', filters, 'no', accessToken),
          getOptions('yearOfIssue', filters, 'attributes', accessToken),
          getOptions('alias', filters, 'attributes', accessToken),
          getOptions('statusCode', filters, 'no', accessToken),
          getOptions('supportedFrom', filters, 'attributes', accessToken),
        ]);

        const documentVerRefacted = documentVersions?.map(v =>
          typeof v === 'string' ? v.replace(/,/g, '_') : v
        );

        setOptions({
          countries: countries.map(option => ({
            value: option,
            label: `${option} (${getCountryName(option)})`,
          })),
          documentTypes: formatOptions(documentTypes),
          states: formatOptions(states),
          documentVersions: formatOptions(documentVerRefacted),
          yearsOfIssue: formatOptions(yearsOfIssue),
          aliases: formatOptions(aliases),
          statusCodes: formatOptions(statusCodes),
          supportedFrom: formatOptions(supportedFrom),
        });

        setIsLoading(false);
      } catch (error) {
        setError(createErrorElement(error, 'fetchAllOptions'));
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const updateSpecificOptions = useCallback(
    async (filters, changedKey, currentOptions) => {
      try {
        setError(null);

        const optionsToUpdate = [];
        const optionKeys = [
          'countryCode',
          'documentType',
          'state',
          'documentVersion',
          'yearOfIssue',
          'alias',
          'statusCode',
          'supportedFrom',
        ];

        // Only fetch options that need updating
        for (const key of optionKeys) {
          if (key === changedKey || filters[key]?.length === 0) {
            const type = ['yearOfIssue', 'alias', 'supportedFrom'].includes(key)
              ? 'attributes'
              : 'no';
            optionsToUpdate.push(getOptions(key, filters, type, accessToken));
          } else {
            optionsToUpdate.push(Promise.resolve(null)); // Keep existing
          }
        }

        const results = await Promise.all(optionsToUpdate);

        const updatedOptions = {...currentOptions};

        results.forEach((result, index) => {
          if (result !== null) {
            const key = optionKeys[index];

            if (key === 'countryCode') {
              updatedOptions.countries = result.map(option => ({
                value: option,
                label: `${option} (${getCountryName(option)})`,
              }));
            } else if (key === 'documentVersion') {
              const refactored = result?.map(v =>
                typeof v === 'string' ? v.replace(/,/g, '_') : v
              );
              updatedOptions.documentVersions = formatOptions(refactored);
            } else {
              const optionMap = {
                documentType: 'documentTypes',
                state: 'states',
                yearOfIssue: 'yearsOfIssue',
                alias: 'aliases',
                statusCode: 'statusCodes',
                supportedFrom: 'supportedFrom',
              };
              updatedOptions[optionMap[key]] = formatOptions(result);
            }
          }
        });

        setOptions(updatedOptions);
      } catch (error) {
        setError(createErrorElement(error, 'updateSpecificOptions'));
      }
    },
    [accessToken]
  );

  const memoizedOptions = useMemo(() => options, [options]);

  return {
    options: memoizedOptions,
    isLoading,
    error,
    setError,
    fetchAllOptions,
    updateSpecificOptions,
  };
};
